import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { Table, TD, TH } from '../../components/table/table';
import {
  ICarrier,
  IDelivery,
  IInvoice,
  IOrder,
  IShipment,
} from '../../graphql/types';
import { Black2, Grey, MangoBlue, White1 } from '../../styles/colours';
import { Medium } from '../../styles/fonts';
import { FormattedDate } from '../formatted-date/formatted-date';
import { Quantity } from '../quantity/quantity';
import { Delivered, TMSUpdate } from './delivery-statuses';

const DeliverySection = styled.section`
  display: flex;
  flex-direction: row;
  margin-bottom: 0.25rem;

  background: ${White1};
`;

const DataRow = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
`;

const DataRowWithBorder = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  min-width: 10rem;
  border-top: 3.5px solid ${MangoBlue};
  margin-bottom: 0.25rem;
`;

const GoodsIssueContainer = styled.div`
  margin-right: 1.5rem;
`;

const DataColumn = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 0.25rem;
  flex: 1;
`;

const HeadCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;

  min-width: 10rem;
  padding: 0.5rem;

  font-family: ${Medium};
  margin: 1rem;

  border-right: 0.5px solid ${Grey};
`;

const BodyCell = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  max-width: 10rem;
  padding: 0.5rem;
`;

const DataCell = styled.div`
  display: flex;
  flex-direction: column;

  min-width: 10rem;
  padding: 0.5rem;
`;

const DataePODCell = styled.div`
  display: flex;
  flex-direction: column;
  position: fixed;
  right: 110px;
  margin-top: 8px;
`;

const DataLabel = styled.div`
  color: ${Black2};
  font-size: 1rem;
`;

const DataLabelHighlighted = styled.div`
  color: ${MangoBlue};
  font-size: 1rem;
`;

const PrimaryDataValue = styled.div`
  font-family: ${Medium};
  font-size: 1.2rem;
  padding: 0.5rem 0;
`;

const DataValue = styled.div`
  font-family: ${Medium};
  font-size: 1rem;
`;
const TruncatedDataValue = styled.div`
  font-family: ${Medium};
  font-size: 1rem;

  max-width: 12rem;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const DataValueHighlight = styled.div`
  font-family: ${Medium};
  color: ${MangoBlue};
  font-size: 1rem;
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const DataLink = styled.span`
  color: blue;
  text-decoration: underline;
  cursor: pointer;
`;

export const CarrierMeta: FC<{ carrier: ICarrier }> = ({ carrier }) => {
  return (
    <DataColumn data-type="carrier-details">
      <DataCell>
        <DataLabel>Carrier</DataLabel>
        <TruncatedDataValue
          data-type="carrier-description"
          title={carrier?.description}
        >
          {carrier?.description}
        </TruncatedDataValue>
      </DataCell>
    </DataColumn>
  );
};

export const ShipmentMeta: FC<{ shipment: IShipment }> = ({ shipment }) => {
  return (
    <DataColumn data-type="shipment-details">
      <DataCell>
        <DataLabel>Shipment Number</DataLabel>
        <DataValue data-type="shipment-id">{shipment?.id}</DataValue>
      </DataCell>
      {shipment?.createdOn && (
        <DataCell>
          <DataLabel>Created on</DataLabel>
          <DataValue data-type="shipment-created-on">
            <FormattedDate date={shipment?.createdOn} />
          </DataValue>
        </DataCell>
      )}
    </DataColumn>
  );
};

export const DeliveryMeta: FC<{ delivery: IDelivery; action: any }> = ({
  delivery,
  action,
}) => {
  return (
    <DataColumn data-type="delivery-details">
      {(delivery.sourceApplication !== 'GSAP' || delivery.epodURL === null) &&
        delivery.sourceApplication !== 'SkyBitz' &&
        delivery.sourceApplication !== 'APM00113877' && (
          <DataCell>
            <DataLabel>Delivery Number</DataLabel>
            <DataValue data-type="delivery-id">
              <DataLink onClick={() => action()}>
                {delivery?.deliveryId}
              </DataLink>
            </DataValue>
          </DataCell>
        )}
      {delivery?.sourceApplication === 'SkyBitz' && (
        <DataCell>
          <DataLabel>Delivery Number</DataLabel>
          <DataValue data-type="delivery-id">{delivery?.deliveryId}</DataValue>
        </DataCell>
      )}
      {delivery?.createdOn && (
        <DataCell>
          <DataLabel>Created on</DataLabel>
          <DataValue data-type="delivery-created-on">
            <FormattedDate date={delivery?.createdOn} />
          </DataValue>
        </DataCell>
      )}
    </DataColumn>
  );
};

export const GoodsIssueMeta: FC<{ delivery: IDelivery }> = ({ delivery }) => {
  return (
    <DataColumn data-type="goods-issue-details">
      <DataCell>
        <DataLabel>Goods issue date</DataLabel>
        <DataValue data-type="goods-issue-date">
          <FormattedDate date={delivery.goodsIssueDate} />
        </DataValue>
      </DataCell>
    </DataColumn>
  );
};

export const InvoiceMeta: FC<{ invoice: IInvoice }> = ({ invoice }) => {
  return (
    <DataColumn data-type="invoice-details">
      <DataCell>
        <DataLabel>Invoice #</DataLabel>
        <DataValue data-type="invoice-id">{invoice?.id}</DataValue>
      </DataCell>
      {invoice?.createdOn && (
        <DataCell>
          <DataLabel>Created on</DataLabel>
          <DataValue data-type="invoice-created-on">
            <FormattedDate date={invoice?.createdOn} />
          </DataValue>
        </DataCell>
      )}
    </DataColumn>
  );
};

export const TrackingIdNumber: FC<{ delivery: IDelivery }> = ({ delivery }) => {
  return (
    <DataColumn>
      <DataCell>
        <DataLabel>Tracking ID</DataLabel>
        <DataValue data-type="tracking-id-number">
          {delivery.trackingIdNumber}
        </DataValue>
      </DataCell>
    </DataColumn>
  );
};

export const DeliveryData: FC<{ delivery: IDelivery; order: IOrder }> = ({
  delivery,
  order,
}) => {
  const [showItem, setShowItem] = useState(false);

  const showItemHandler = () => {
    setShowItem(!showItem);
  };

  const filterById: string[] = [];
  filterById.push(delivery.deliveryId);
  const deliveryIdItems = order.items?.filter(item =>
    item.deliveries.find(id => {
      return filterById.includes(id.deliveryId);
    })
  );
  return (
    <div>
      <DeliverySection data-type="delivery">
        <HeadCell>
          {(delivery.sourceApplication === 'SkyBitz' ||
            delivery.sourceApplication === 'APM00113877') && (
            <DataLabel>
              TMS
              <br />
              <br />
            </DataLabel>
          )}
          {delivery.sourceApplication !== 'GSAP' &&
            (delivery.status.toLowerCase() === 'delivered' ? (
              <Delivered />
            ) : (
              <TMSUpdate update={delivery.status} />
            ))}
          {delivery.sourceApplication === 'GSAP' && (
            <DataLabel>GSAP Outbound</DataLabel>
          )}
        </HeadCell>
        <BodyCell>
          <DataColumn>
            <DataRow>
              {(delivery.sourceApplication !== 'GSAP' ||
                delivery.epodURL === null) &&
                delivery.sourceApplication !== 'APM00113877' && (
                  <DataCell>
                    <DataLabel>Quantity:</DataLabel>
                    <PrimaryDataValue data-type="delivery-quantity">
                      <Quantity
                        quantity={delivery.quantity}
                        unitOfMeasure={delivery.unitOfMeasure}
                      />
                    </PrimaryDataValue>
                  </DataCell>
                )}
              {delivery?.epodURL && (
                <DataePODCell>
                  <DataValue data-type="shipment-epod-url">
                    <a
                      href={delivery.epodURL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      ePOD
                    </a>
                  </DataValue>
                </DataePODCell>
              )}
              {delivery?.provenOn && (
                <DataCell>
                  <DataLabel>Delivered on:</DataLabel>
                  <PrimaryDataValue>
                    <FormattedDate date={delivery?.provenOn} />
                  </PrimaryDataValue>
                </DataCell>
              )}
            </DataRow>
            <DataRow>
              {delivery && (
                <DeliveryMeta delivery={delivery} action={showItemHandler} />
              )}
              {delivery.shipment && (
                <ShipmentMeta shipment={delivery.shipment} />
              )}
              {delivery.goodsIssueDate && (
                <GoodsIssueContainer>
                  <GoodsIssueMeta delivery={delivery} />
                </GoodsIssueContainer>
              )}
              {delivery.invoice && <InvoiceMeta invoice={delivery.invoice} />}
              <StyledContainer>
                {delivery.carrier && <CarrierMeta carrier={delivery.carrier} />}
                {delivery.trackingIdNumber && (
                  <TrackingIdNumber delivery={delivery} />
                )}
              </StyledContainer>
            </DataRow>
            {delivery.tmsOrKeplerDeliveryEvent && (
              <DataRow>
                {delivery.tmsOrKeplerDeliveryEvent &&
                  delivery.tmsOrKeplerDeliveryEvent.deliveryDate && (
                    <DataCell>
                      {delivery.sourceApplication !== 'APM00113877' && (
                        <DataRowWithBorder />
                      )}
                      <DataLabelHighlighted>ETA:</DataLabelHighlighted>
                      <DataValueHighlight data-type="tms-delivery-date">
                        <FormattedDate
                          date={delivery.tmsOrKeplerDeliveryEvent.deliveryDate}
                        />
                        ,
                        {' ' +
                          delivery.tmsOrKeplerDeliveryEvent.deliveryDate
                            .slice(11)
                            .slice(0, 5)}
                      </DataValueHighlight>
                    </DataCell>
                  )}
                {delivery.tmsOrKeplerDeliveryEvent &&
                  delivery.tmsOrKeplerDeliveryEvent.timeToDelivery && (
                    <DataCell>
                      <DataLabel>Time left to Delivery:</DataLabel>
                      <DataValueHighlight data-type="tms-time-to-delivery">
                        {delivery.tmsOrKeplerDeliveryEvent.timeToDelivery}
                      </DataValueHighlight>
                    </DataCell>
                  )}
                {delivery.tmsOrKeplerDeliveryEvent &&
                  delivery.tmsOrKeplerDeliveryEvent.vehicleDistanceUnit && (
                    <DataCell>
                      <DataLabel>Vehicle Distance:</DataLabel>
                      <DataValueHighlight data-type="vehicle-distance-unit">
                        {parseFloat(
                          delivery.tmsOrKeplerDeliveryEvent.vehicleDistance
                        ).toFixed(1) +
                          ' ' +
                          delivery.tmsOrKeplerDeliveryEvent.vehicleDistanceUnit}
                      </DataValueHighlight>
                    </DataCell>
                  )}
              </DataRow>
            )}
          </DataColumn>
        </BodyCell>
      </DeliverySection>
      {showItem && (
        <Table>
          <thead>
            <tr>
              <TH>Order line item</TH>
              <TH>Material number</TH>
              <TH>Ordered Quantity</TH>
              <TH>Confirmed Quantity</TH>
              <TH>SKU</TH>
              <TH>First date</TH>
              <TH>Material description</TH>
              <TH>Plant</TH>
            </tr>
          </thead>
          <tbody>
            {deliveryIdItems?.map(item => (
              <tr key={item.index}>
                <TD>{item.index}</TD>
                <TD>{item.product.id}</TD>
                <TD>{item.desiredQuantity}</TD>
                <TD>
                  {item.deliveries.map((q, i) => {
                    if (q.deliveryId === delivery.deliveryId) {
                      return item.schedules[i].confirmedQuantity;
                    }
                  })}
                </TD>
                <TD>{item.unitOfMeasure}</TD>
                <TD>
                  {item.deliveries.map((q, i) => {
                    if (q.deliveryId === delivery.deliveryId) {
                      return (
                        <FormattedDate
                          key={item.index}
                          date={item.schedules[i].promiseDate}
                        />
                      );
                    }
                  })}
                </TD>
                <TD>{item.product.description}</TD>
                <TD>
                  <span>{item.plant.id + '\n' + item.plant.description}</span>
                </TD>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
};
